import React, { createContext, HTMLAttributes, ReactElement, useContext, useId } from "react";
import { NavLink, To, useLocation } from "react-router-dom";
import { ChevronRight, KeyboardDoubleArrowRight } from "@mui/icons-material";
import {
  alpha,
  Box,
  IconButton,
  Link,
  List,
  Divider as MuiDivider,
  Stack,
  styled,
  SxProps,
  Tooltip,
  useTheme,
} from "@mui/material";
import { OverridableComponent, OverridableTypeMap } from "@mui/material/OverridableComponent";
import { SimpleTreeView, TreeItem2 } from "@mui/x-tree-view";
import { HEADER_HEIGHT } from "~/components/AppHeader";
import TextOverflow from "~/components/TextOverflow";
import theme from "~/theme";

export const COLLAPSED_WIDTH = "57px";
export const EXPANDED_WIDTH = "210px";

const SideNavContext = createContext<{ width: string }>({ width: EXPANDED_WIDTH });

export const useSideNavWidth = () => useContext(SideNavContext);

export interface NavItemProps {
  title: string;
  to: To | undefined;
  icon?: ReactElement;
  overrideHighlight?: string | false;
  showTitle?: boolean;
  tooltip?: ReactElement | string;
  sx?: SxProps;
  subItems?: Array<{
    title: string;
    to: To;
  }>;
}

const StyledLink = styled(Link)({
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  svg: {
    height: 22,
    width: 22,
  },
  "& > div": {
    backgroundColor: "transparent",
    transition: "background-color .2s linear",
  },
  "&:hover > div": {
    backgroundColor: "#e4e4e6",
  },
  "&.active > div": {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.1)}`,
    "svg, p": {
      fontWeight: 500,
      color: theme.palette.primary.main,
    },
  },
}) as OverridableComponent<OverridableTypeMap>;

const FormattedTitle = ({ content }: { content: React.ReactNode }) => {
  if (typeof content === "string") {
    return <TextOverflow variant="body2">{content}</TextOverflow>;
  } else {
    return <>{content}</>;
  }
};

type NavItemContentProps = Pick<NavItemProps, "to" | "tooltip" | "icon" | "showTitle" | "title"> & {
  highlight: boolean;
  hasSubItems?: boolean;
  sx?: SxProps;
  linkSx?: SxProps;
} & HTMLAttributes<HTMLElement>;

const NavItemContent = ({
  to,
  tooltip,
  icon,
  showTitle,
  title,
  highlight,
  hasSubItems,
  sx,
  linkSx,
  ...props
}: NavItemContentProps) => {
  return (
    <Tooltip title={tooltip} enterDelay={500} placement="right" arrow disableInteractive>
      <Box
        display="flex"
        width="100%"
        sx={{
          ...sx,
          "& #chevron": { rotate: "90deg", transition: "all 150ms" },
          "&.Mui-expanded #chevron": { rotate: "270deg" },
        }}
        {...props}
      >
        <StyledLink
          component={to ? NavLink : Box}
          to={to}
          className={highlight ? "active" : undefined}
          sx={{ display: "block", width: "100%", cursor: "pointer", ...linkSx }}
          aria-label={title}
          underline="none"
        >
          <Box
            display="flex"
            gap={1}
            px={showTitle ? 1.125 : 0}
            height={40}
            minWidth={40}
            alignItems="center"
            whiteSpace="nowrap"
            justifyContent={showTitle ? "left" : "center"}
            borderRadius="6px"
            color="primary.dark"
            sx={{
              "& svg": {
                flexShrink: 0,
              },
            }}
          >
            {icon}
            {showTitle && <FormattedTitle content={title} />}
            {hasSubItems && <ChevronRight id="chevron" sx={{ marginLeft: "auto" }} />}
          </Box>
        </StyledLink>
      </Box>
    </Tooltip>
  );
};

export const NavItem = ({
  title,
  to,
  icon,
  overrideHighlight,
  showTitle,
  tooltip,
  sx = {},
  subItems,
}: NavItemProps) => {
  const { pathname } = useLocation();
  // Optionally extend default react-router NavLink matching behavior
  const highlight = overrideHighlight ? pathname.startsWith(overrideHighlight) : false;
  const itemId = useId();

  if (subItems && subItems.length > 0) {
    return (
      <TreeItem2
        itemId={itemId}
        slots={{
          content: NavItemContent,
        }}
        slotProps={{
          content: {
            to,
            tooltip,
            showTitle,
            title,
            icon,
            highlight,
            hasSubItems: true,
          } as NavItemContentProps,
        }}
      >
        {subItems.map((subItem) => {
          const subItemId = `${itemId}-subItem-${subItem.to}`;
          return (
            <TreeItem2
              key={subItemId}
              itemId={subItemId}
              slots={{
                content: NavItemContent,
              }}
              slotProps={{
                root: { style: { display: "flex" } },
                content: {
                  to: subItem.to,
                  title: subItem.title,
                  showTitle: true,
                  highlight,
                  sx: {
                    height: 32,
                    borderLeft: "1px solid #E5E5E5",
                    display: "flex",
                    alignItems: "center",
                    ml: 1,
                    pl: 1,
                    pr: 3,
                    "& a > div": {
                      height: 28,
                    },
                  },
                } as NavItemContentProps,
              }}
            />
          );
        })}
      </TreeItem2>
    );
  }

  return (
    <Tooltip title={tooltip} enterDelay={500} placement="right" arrow disableInteractive>
      <Box component="li" display="flex" width="100%">
        <StyledLink
          component={NavLink}
          to={to}
          className={highlight ? "active" : undefined}
          sx={{ display: "block", width: "100%", ...sx }}
          aria-label={title}
          underline="none"
        >
          <Box
            display="flex"
            gap={1}
            px={showTitle ? 1.125 : 0}
            height={40}
            minWidth={40}
            alignItems="center"
            whiteSpace="nowrap"
            justifyContent={showTitle ? "left" : "center"}
            borderRadius="6px"
            color="primary.dark"
            sx={{
              "& svg": {
                flexShrink: 0,
              },
            }}
          >
            {icon}
            {showTitle && <FormattedTitle content={title} />}
          </Box>
        </StyledLink>
      </Box>
    </Tooltip>
  );
};

const Divider = () => (
  <MuiDivider flexItem sx={{ bgcolor: "primary.dark", opacity: 0.125, mx: 0.5, my: 0.25 }} />
);

const SideNav = ({
  sections,
  collapsed = false,
  onCollapseToggle,
}: HTMLAttributes<HTMLElement> & {
  sections: (ReactElement<NavItemProps>[] | ReactElement<NavItemProps>)[];
  collapsed: boolean;
  onCollapseToggle: () => void;
}) => {
  const theme = useTheme();
  const transitionDuration = collapsed
    ? theme.transitions.duration.leavingScreen
    : theme.transitions.duration.enteringScreen;

  const currentWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <SideNavContext.Provider value={{ width: currentWidth }}>
      <Box
        component="nav"
        sx={{
          pt: 1,
          top: 0,
          left: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "sticky",
          zIndex: theme.zIndex.appBar,
          flex: `0 0 ${currentWidth}`,
          transition: `flex ${transitionDuration}ms`,
          height: `calc(100vh - ${HEADER_HEIGHT})`,
          bgcolor: "common.toolbarBackground",
          overflowX: "hidden",
          overflowY: "auto",
          borderRight: `1px solid ${theme.palette.common.toolbarBorder}`,
          "& li": {
            width: collapsed ? "fit-content" : "100%",
          },
        }}
      >
        <SimpleTreeView>
          <Stack component={List} disablePadding width="100%" gap={0.5} px={1}>
            {sections.flatMap((section, index) => {
              const last = index === sections.length - 1;
              if (!last) {
                return [section, <Divider key={`divider-${index}`} />];
              }
              return [section];
            })}
          </Stack>
        </SimpleTreeView>
        <Box
          pl="15px"
          pt={1}
          pb={1}
          mb={0.5}
          position="sticky"
          bottom={0}
          bgcolor="common.toolbarBackground"
        >
          <IconButton
            aria-label="toggle navigation collapse"
            onClick={onCollapseToggle}
            sx={{
              "& svg": {
                fontSize: 18,
                opacity: 0.5,
                transition: `transform ${transitionDuration}`,
                transform: `rotate${collapsed ? "(0)" : "(180deg)"}`,
              },
              "&:hover svg": {
                opacity: 1,
              },
            }}
          >
            <KeyboardDoubleArrowRight />
          </IconButton>
        </Box>
      </Box>
    </SideNavContext.Provider>
  );
};

export default SideNav;
